import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";

// MUI v4 has no Campaign (megaphone) glyph — AnnouncementRounded (speech
// bubble with !) is the closest match available. Aliased so call sites
// don't change.
import CampaignRoundedIcon from "@material-ui/icons/AnnouncementRounded";
import AddRoundedIcon from "@material-ui/icons/AddRounded";
import DeleteRoundedIcon from "@material-ui/icons/DeleteRounded";
import RefreshRoundedIcon from "@material-ui/icons/RefreshRounded";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";

import { toast } from "react-toastify";
import api from "../../services/api";
import useCompanies from "../../hooks/useCompanies";
import { i18n } from "../../translate/i18n";

// 16px input font defeats iOS Safari zoom-on-focus (spec A.4/B.7).
const MOBILE_INPUT_PROPS = { style: { fontSize: 16 } };

/**
 * BroadcastManager — super-admin compose + manage view for platform
 * notifications. Surfaces the rows from /api/admin/notifications,
 * lets the operator create a new banner, toggle active state, and
 * delete obsolete ones.
 */

const SEVERITIES = ["info", "success", "warning", "critical"];

// === CRM cardStackMode v3 tokens (verbatim from canonical reference) ===
const SP  = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48 };
const TT  = { min: 44, primary: 48, hero: 56 };

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  // CRM tokens — derived per spec
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  const cardBg     = isDark ? "#13181a" : "#ffffff";

  return {
    root: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: SP.md,
      // Leave room for the floating compose FAB on mobile.
      [theme.breakpoints.down("xs")]: {
        paddingBottom: 96,
        position: "relative",
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: SP.sm,
    },
    headerLeft: { display: "flex", alignItems: "center", gap: SP.sm },
    headerIcon: {
      width: 40,
      height: 40,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: accentSoft,
      border: `1px solid ${hair}`,
      "& .MuiSvgIcon-root": {
        fontSize: 20,
        color: accent,
      },
    },
    title: {
      fontSize: 20,
      fontWeight: 700,
      color: fg,
      lineHeight: 1.25,
      letterSpacing: "-0.01em",
    },
    subtitle: {
      fontSize: 12.5,
      color: fgMuted,
      marginTop: 2,
      lineHeight: 1.4,
    },
    actions: { display: "flex", gap: SP.xs, alignItems: "center" },
    // Desktop refresh — quiet ghost button
    ghostBtn: {
      minHeight: 38,
      borderRadius: 10,
      color: fgMuted,
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: 0,
      "&:hover": {
        background: accentSoft,
        color: accent,
      },
    },
    // Desktop primary "Compose" CTA — solid brand-green
    primary: {
      background: accent,
      color: "#fff",
      borderRadius: 12,
      minHeight: 40,
      padding: "8px 18px",
      fontWeight: 700,
      textTransform: "none",
      letterSpacing: 0,
      boxShadow: isDark
        ? "0 6px 14px rgba(46,227,122,0.22)"
        : "0 8px 20px rgba(18,140,126,0.22)",
      "&:hover": {
        background: isDark ? "#3aed87" : "#0f7a6e",
      },
      // Hide the inline desktop CTA on mobile — the FAB takes over.
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
    // Mobile floating action button — bottom-right, WhatsApp green
    composeFab: {
      display: "none",
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        position: "fixed",
        right: 18,
        bottom: 22,
        zIndex: 1100,
        minWidth: 0,
        width: 60,
        height: 60,
        borderRadius: 30,
        background: accent,
        color: "#fff",
        boxShadow: "0 14px 28px rgba(18,140,126,0.34), 0 6px 12px rgba(15,22,20,0.18)",
        "& .MuiSvgIcon-root": { fontSize: 28 },
        "& .MuiButton-startIcon": { margin: 0 },
        "& .MuiButton-label": { padding: 0 },
        "&:hover": { background: accent, opacity: 0.95 },
      },
    },
    // Hide refresh button on xs to avoid header clutter; FAB is the only mobile CTA
    refreshHideXs: {
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
    // Card stack list container — Paper-like wrapper on desktop, transparent on mobile
    list: {
      background: cardBg,
      border: `1px solid ${hair}`,
      borderRadius: 18,
      overflow: "hidden",
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 14px 36px rgba(0,0,0,0.32)"
        : "0 14px 36px rgba(15,22,20,0.06), 0 2px 6px rgba(15,22,20,0.04)",
      [theme.breakpoints.down("xs")]: {
        background: "transparent",
        border: "none",
        borderRadius: 0,
        boxShadow: "none",
        overflow: "visible",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      },
    },
    // Desktop column header row
    rowHead: {
      display: "grid",
      gridTemplateColumns: "1fr 110px 150px 120px 96px",
      gap: SP.md,
      padding: "16px 18px",
      background: isDark ? "rgba(46,227,122,0.04)" : "rgba(18,140,126,0.04)",
      borderBottom: `1px solid ${hair}`,
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      whiteSpace: "nowrap",
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
    // Desktop data row
    row: {
      display: "grid",
      gridTemplateColumns: "1fr 110px 150px 120px 96px",
      gap: SP.md,
      padding: "16px 18px",
      borderBottom: `1px solid ${hair}`,
      alignItems: "center",
      transition: "background 0.14s ease",
      "&:hover": { background: accentSoft },
      "&:last-child": { borderBottom: "none" },
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
    rowTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: fg,
      lineHeight: 1.35,
      letterSpacing: "-0.005em",
    },
    rowBody: {
      fontSize: 12.5,
      color: fgMuted,
      marginTop: 4,
      maxWidth: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      lineHeight: 1.45,
    },
    severityPill: {
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.12em",
      lineHeight: 1.4,
    },
    audienceLabel: {
      fontSize: 13,
      color: fg,
      fontWeight: 500,
    },
    audienceSub: {
      fontSize: 10.5,
      color: fgMuted,
      marginTop: 2,
      letterSpacing: "0.04em",
    },
    createdAt: {
      fontSize: 12,
      color: fgMuted,
      fontVariantNumeric: "tabular-nums",
      fontFeatureSettings: '"tnum" 1',
    },
    actionsCol: {
      display: "flex",
      gap: 4,
      justifyContent: "flex-end",
      alignItems: "center",
      "& .MuiIconButton-root": {
        color: fgMuted,
        transition: "color 0.14s ease, background 0.14s ease",
        "&:hover": {
          color: accent,
          background: accentSoft,
        },
        "&[data-variant='danger']:hover": {
          color: "#e25c5c",
          background: isDark ? "rgba(255,107,107,0.08)" : "rgba(220,38,38,0.06)",
        },
      },
    },

    // === MOBILE — card-stack v3 ===
    mobileStack: {
      display: "none",
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: "4px 0 24px",
        width: "100%",
        boxSizing: "border-box",
      },
    },
    // === LINEAR-DENSE MOBILE CARD (xs override) ===
    // Spec: 12px padding, 1px hairline border, NO resting shadow,
    // 14px title (13px on <360), no truncation, 28px hit-area icons.
    mobileCard: {
      [theme.breakpoints.down("xs")]: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "12px 14px",
        background: isDark ? "#13181a" : "#ffffff",
        borderRadius: 8,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
        // No resting shadow per spec anti_pattern.no_resting_shadow
        boxShadow: "none",
        transition: "border-color 0.14s ease, box-shadow 0.14s ease",
        // Status dot in top-left for at-a-glance health
        "&::before": {
          content: '""',
          position: "absolute",
          top: 14,
          insetInlineStart: 14,
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "var(--status-dot, #a3a3a3)",
        },
        "&:hover": {
          borderColor: isDark ? "rgba(255,255,255,0.14)" : "#d4d4d4",
          boxShadow: "0 1px 2px rgba(10,10,10,0.04)",
        },
      },
      "@media (max-width: 360px)": {
        padding: "10px 12px",
        gap: 5,
        borderRadius: 8,
      },
    },
    mobileCardInactive: {
      [theme.breakpoints.down("xs")]: {
        opacity: 0.62,
      },
    },
    mobileCardHeader: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        // Indent for status dot
        paddingInlineStart: 12,
      },
    },
    mobileCardHeaderText: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minWidth: 0,
        flex: 1,
      },
    },
    mobileCardMeta: {
      [theme.breakpoints.down("xs")]: {
        fontSize: 11,
        fontWeight: 400,
        letterSpacing: 0,
        textTransform: "none",
        color: isDark ? "rgba(255,255,255,0.45)" : "#a3a3a3",
        fontFeatureSettings: '"tnum" 1',
        lineHeight: "14px",
      },
      "@media (max-width: 360px)": {
        fontSize: 10,
        lineHeight: "13px",
      },
    },
    mobileCardTitle: {
      [theme.breakpoints.down("xs")]: {
        fontSize: 14,
        fontWeight: 500,
        lineHeight: "18px",
        color: isDark ? "rgba(255,255,255,0.92)" : "#18181b",
        letterSpacing: "-0.005em",
        // NEVER truncate — wrap multi-line
        overflow: "visible",
        textOverflow: "clip",
        whiteSpace: "normal",
        wordBreak: "normal",
        overflowWrap: "anywhere",
      },
      "@media (max-width: 360px)": {
        fontSize: 13,
        lineHeight: "17px",
      },
    },
    mobileStatusPill: {
      [theme.breakpoints.down("xs")]: {
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 6px",
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 500,
        textTransform: "none",
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
        lineHeight: "13px",
      },
    },
    mobileCardBody: {
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        lineHeight: "16px",
        fontWeight: 400,
        color: isDark ? "rgba(255,255,255,0.55)" : "#52525b",
        // NEVER truncate; wrap fully
        overflow: "visible",
        textOverflow: "clip",
        display: "block",
        wordBreak: "normal",
        overflowWrap: "anywhere",
        paddingInlineStart: 12,
      },
    },
    mobileCardAudience: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "row",
        alignItems: "baseline",
        gap: 6,
        paddingInlineStart: 12,
      },
    },
    mobileCardAudienceLabel: {
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        fontWeight: 400,
        color: isDark ? "rgba(255,255,255,0.92)" : "#18181b",
      },
    },
    mobileCardAudienceSub: {
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: 0,
        textTransform: "none",
        color: isDark ? "rgba(255,255,255,0.45)" : "#71717a",
        minWidth: 64,
      },
    },
    // KPI footer — hairline top, compact KV row
    mobileCardFooter: {
      [theme.breakpoints.down("xs")]: {
        marginTop: 2,
        paddingTop: 8,
        borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#ededed"}`,
        display: "grid",
        gridTemplateColumns: "1fr 1fr auto",
        gap: 8,
        alignItems: "center",
        paddingInlineStart: 12,
        "& > *": {
          minWidth: 0,
        },
      },
    },
    mobileKpi: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 0,
      },
    },
    mobileKpiLabel: {
      [theme.breakpoints.down("xs")]: {
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: 0,
        textTransform: "none",
        color: isDark ? "rgba(255,255,255,0.45)" : "#71717a",
        lineHeight: "13px",
      },
    },
    mobileKpiValue: {
      [theme.breakpoints.down("xs")]: {
        fontSize: 12,
        fontWeight: 400,
        lineHeight: "16px",
        color: isDark ? "rgba(255,255,255,0.92)" : "#18181b",
        fontFeatureSettings: '"tnum" 1',
        letterSpacing: 0,
        // No truncation — wrap if needed
        overflow: "visible",
        textOverflow: "clip",
        whiteSpace: "normal",
        minWidth: 0,
      },
    },
    // Mobile card action row (toggle-active + delete). Icon glyph stays
    // 16px, but each IconButton's hit target expands to 44x44 on xs per
    // WCAG / iOS HIG (Raviv 2026-06-05; same pattern as
    // pages/Connections tableIconBtn).
    mobileCardActions: {
      [theme.breakpoints.down("xs")]: {
        display: "flex",
        alignItems: "center",
        gap: 2,
        "& .MuiIconButton-root": {
          minWidth: TT.min,
          minHeight: TT.min,
          padding: 10,
          borderRadius: 4,
          color: isDark ? "rgba(255,255,255,0.55)" : "#a3a3a3",
          transition: "color 0.14s ease, background 0.14s ease",
          "& .MuiSvgIcon-root": { fontSize: 16 },
          "&:hover": {
            color: isDark ? "rgba(255,255,255,0.92)" : "#0a0a0a",
            background: isDark ? "rgba(255,255,255,0.06)" : "#f4f4f5",
          },
          "&[data-variant='danger']:hover": {
            color: "#dc2626",
            background: isDark ? "rgba(255,107,107,0.08)" : "rgba(220,38,38,0.06)",
          },
        },
      },
    },

    // Empty state — CRM unified pattern
    emptyState: {
      padding: "56px 24px 48px",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 12,
      "& .emptyNum": {
        fontSize: 56,
        fontWeight: 400,
        letterSpacing: "-0.02em",
        color: accent,
        lineHeight: 1,
        opacity: 0.85,
        fontFeatureSettings: '"tnum" 1',
      },
      "& .emptyMeta": {
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: accent,
      },
      "& .emptyDesc": {
        fontSize: 14,
        lineHeight: 1.5,
        color: fgMuted,
        maxWidth: 280,
        margin: 0,
      },
    },
    loadingWrap: {
      display: "grid",
      placeItems: "center",
      minHeight: 200,
      padding: 24,
    },
    composeBox: {
      display: "flex",
      flexDirection: "column",
      gap: 14,
      paddingTop: 8,
    },
    rowControls: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    // DialogActions: stack column-reverse on xs (B.5) so primary CTA
    // (Publish) lands under the thumb. Full-width 48px on mobile.
    dialogActionsMobile: {
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column-reverse",
        padding: 16,
        gap: 8,
        "& > *": {
          width: "100%",
          minHeight: TT.primary,
          marginInlineStart: "0 !important",
        },
      },
    },
    // Animation token (shared CRM card-in keyframe)
    "@keyframes broadcastCardIn": {
      "0%":   { opacity: 0, transform: "translateY(8px)" },
      "100%": { opacity: 1, transform: "translateY(0)" },
    },
  };
});

const SEVERITY_COLOR = {
  info: { bg: "rgba(96,165,250,0.18)", fg: "#1d4ed8" },
  success: { bg: "rgba(46,227,122,0.18)", fg: "#0e6c61" },
  warning: { bg: "rgba(251,191,36,0.18)", fg: "#92400e" },
  critical: { bg: "rgba(252,165,165,0.18)", fg: "#b91c1c" },
};

// Aggregate status — Sent / Scheduled / Failed / Inactive — derived from
// active flag + endsAt + severity. Mobile status pill uses these palettes.
const STATUS_TONE = {
  sent:      { bg: "rgba(46,227,122,0.16)", fg: "#0e6c61", label: "Sent" },
  scheduled: { bg: "rgba(251,191,36,0.18)", fg: "#92400e", label: "Scheduled" },
  failed:    { bg: "rgba(252,165,165,0.22)", fg: "#b91c1c", label: "Failed" },
  inactive:  { bg: "rgba(100,116,139,0.16)", fg: "#475569", label: "Inactive" },
};

const deriveStatus = (row) => {
  if (!row.active) return "inactive";
  if (row.severity === "critical") return "failed";
  if (row.endsAt && new Date(row.endsAt).getTime() > Date.now()) return "scheduled";
  return "sent";
};

const formatDateShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "2-digit",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return iso;
  }
};

const BroadcastManager = () => {
  const classes = useStyles();
  const theme = useTheme();
  // fullScreen compose Dialog on xs (3+ form fields).
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const companiesHook = useCompanies();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);

  const [form, setForm] = useState({
    title: "",
    body: "",
    severity: "info",
    audience: "all",
    targetCompanyId: "",
    endsAt: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/notifications");
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      toast.error(
        i18n.t("admin.broadcast.loadError", {
          defaultValue: "Could not load notifications.",
        })
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    companiesHook
      .list()
      .then((list) => setAllCompanies((list || []).filter((c) => c.id !== 1)))
      .catch(() => setAllCompanies([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleActive = async (row) => {
    try {
      await api.patch(`/api/admin/notifications/${row.id}`, {
        active: !row.active,
      });
      await load();
    } catch (e) {
      toast.error(
        i18n.t("admin.broadcast.actionFailed", { defaultValue: "Action failed" })
      );
    }
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        i18n.t("admin.broadcast.confirmDelete", {
          title: row.title,
          defaultValue: `Delete "${row.title}"? This cannot be undone.`,
        })
      )
    ) {
      return;
    }
    try {
      await api.delete(`/api/admin/notifications/${row.id}`);
      await load();
    } catch (e) {
      toast.error(
        i18n.t("admin.broadcast.actionFailed", { defaultValue: "Action failed" })
      );
    }
  };

  const resetForm = () => {
    setForm({
      title: "",
      body: "",
      severity: "info",
      audience: "all",
      targetCompanyId: "",
      endsAt: "",
    });
  };

  const handleCompose = async () => {
    if (submitting) return;
    if (!form.title.trim() || !form.body.trim()) {
      toast.error(
        i18n.t("admin.broadcast.titleBodyRequired", {
          defaultValue: "Title and body are required.",
        })
      );
      return;
    }
    if (form.audience === "tenant" && !form.targetCompanyId) {
      toast.error(
        i18n.t("admin.broadcast.tenantTargetRequired", {
          defaultValue: "Pick a target company for tenant-scoped broadcasts.",
        })
      );
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/admin/notifications", {
        title: form.title.trim(),
        body: form.body.trim(),
        severity: form.severity,
        audience: form.audience,
        targetCompanyId:
          form.audience === "tenant" ? Number(form.targetCompanyId) : null,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      });
      toast.success(
        i18n.t("admin.broadcast.created", {
          defaultValue: "Notification published.",
        })
      );
      setComposeOpen(false);
      resetForm();
      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.error ||
          i18n.t("admin.broadcast.createFailed", {
            defaultValue: "Could not publish notification.",
          })
      );
    }
    setSubmitting(false);
  };

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      // Active first, then newest first.
      if (a.active !== b.active) return a.active ? -1 : 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [rows]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <span className={classes.headerIcon}>
            <CampaignRoundedIcon />
          </span>
          <Box>
            <Typography className={classes.title} component="div">
              {i18n.t("admin.broadcast.title", {
                defaultValue: "Broadcasts",
              })}
            </Typography>
            <Typography className={classes.subtitle} component="div">
              {i18n.t("admin.broadcast.subtitle", {
                defaultValue: "Platform-wide or per-tenant announcement banners",
              })}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.actions}>
          <Button
            size="small"
            className={`${classes.ghostBtn} ${classes.refreshHideXs}`}
            startIcon={<RefreshRoundedIcon />}
            onClick={load}
          >
            {i18n.t("admin.broadcast.refresh", { defaultValue: "Refresh" })}
          </Button>
          <Button
            size="small"
            variant="contained"
            className={classes.primary}
            startIcon={<AddRoundedIcon />}
            onClick={() => setComposeOpen(true)}
          >
            {i18n.t("admin.broadcast.compose", {
              defaultValue: "New broadcast",
            })}
          </Button>
        </Box>
      </Box>

      {/* Desktop / tablet table-style list */}
      <Box className={classes.list}>
        <Box className={classes.rowHead}>
          <span>{i18n.t("admin.broadcast.col.title", { defaultValue: "Message" })}</span>
          <span>{i18n.t("admin.broadcast.col.severity", { defaultValue: "Severity" })}</span>
          <span>{i18n.t("admin.broadcast.col.audience", { defaultValue: "Audience" })}</span>
          <span>{i18n.t("admin.broadcast.col.created", { defaultValue: "Created" })}</span>
          <span style={{ textAlign: "right" }}>
            {i18n.t("admin.broadcast.col.actions", { defaultValue: "Actions" })}
          </span>
        </Box>
        {loading ? (
          <Box className={classes.loadingWrap}>
            <CircularProgress size={24} style={{ color: "#128c7e" }} />
          </Box>
        ) : sortedRows.length === 0 ? (
          <Box className={classes.emptyState}>
            <div className="emptyNum">0</div>
            <div className="emptyMeta">
              {i18n.t("admin.broadcast.emptyMeta", { defaultValue: "No broadcasts yet" })}
            </div>
            <p className="emptyDesc">
              {i18n.t("admin.broadcast.empty", {
                defaultValue: "Compose the first announcement to start broadcasting.",
              })}
            </p>
          </Box>
        ) : (
          sortedRows.map((row) => {
            const tone = SEVERITY_COLOR[row.severity] || SEVERITY_COLOR.info;
            return (
              <Box key={row.id} className={classes.row}>
                <Box>
                  <Typography className={classes.rowTitle} component="div">
                    {row.title}
                  </Typography>
                  <Typography className={classes.rowBody} component="div">
                    {row.body}
                  </Typography>
                </Box>
                <span
                  className={classes.severityPill}
                  style={{ background: tone.bg, color: tone.fg }}
                >
                  {row.severity}
                </span>
                <Box>
                  <Typography className={classes.audienceLabel}>
                    {row.audience === "all"
                      ? i18n.t("admin.broadcast.audience.all", {
                          defaultValue: "All tenants",
                        })
                      : row.targetCompany?.name || "—"}
                  </Typography>
                  {row.endsAt && (
                    <Typography className={classes.audienceSub}>
                      {i18n.t("admin.broadcast.endsOn", {
                        date: formatDateShort(row.endsAt),
                        defaultValue: `until ${formatDateShort(row.endsAt)}`,
                      })}
                    </Typography>
                  )}
                </Box>
                <Typography className={classes.createdAt}>
                  {formatDateShort(row.createdAt)}
                </Typography>
                <Box className={classes.actionsCol}>
                  <Tooltip
                    title={
                      row.active
                        ? i18n.t("admin.broadcast.tooltip.active", {
                            defaultValue: "Active (tap to stop)",
                          })
                        : i18n.t("admin.broadcast.tooltip.stopped", {
                            defaultValue: "Stopped (tap to activate)",
                          })
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(row)}
                      role="switch"
                      aria-checked={!!row.active}
                      aria-label={
                        row.active
                          ? i18n.t("admin.broadcast.aria.active", {
                              defaultValue: "Broadcast is active",
                            })
                          : i18n.t("admin.broadcast.aria.stopped", {
                              defaultValue: "Broadcast is stopped",
                            })
                      }
                      style={{ color: row.active ? "#25d366" : "#94a3b8" }}
                    >
                      <PowerSettingsNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(row)}
                    data-variant="danger"
                    aria-label={i18n.t("admin.broadcast.deleteAria", {
                      defaultValue: "Delete broadcast",
                    })}
                  >
                    <DeleteRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Mobile card stack — cardStackMode v3 */}
      <Box className={classes.mobileStack}>
        {loading ? (
          <Box className={classes.loadingWrap}>
            <CircularProgress size={24} style={{ color: "#128c7e" }} />
          </Box>
        ) : sortedRows.length === 0 ? (
          <Box className={classes.emptyState}>
            <div className="emptyNum">0</div>
            <div className="emptyMeta">
              {i18n.t("admin.broadcast.emptyMeta", { defaultValue: "No broadcasts yet" })}
            </div>
            <p className="emptyDesc">
              {i18n.t("admin.broadcast.empty", {
                defaultValue: "Tap the + button to compose your first announcement.",
              })}
            </p>
          </Box>
        ) : (
          sortedRows.map((row, idx) => {
            const status = deriveStatus(row);
            const statusTone = STATUS_TONE[status];
            const sentDateLabel = formatDateShort(row.createdAt);
            const scheduledLabel = row.endsAt
              ? formatDateShort(row.endsAt)
              : null;
            const preview =
              row.body && row.body.length > 100
                ? `${row.body.slice(0, 100).trim()}…`
                : row.body || "";
            const recipientName =
              row.audience === "all"
                ? i18n.t("admin.broadcast.audience.all", {
                    defaultValue: "All tenants",
                  })
                : row.targetCompany?.name || "—";

            return (
              <Box
                key={row.id}
                className={`${classes.mobileCard} ${!row.active ? classes.mobileCardInactive : ""}`}
                style={{ "--i": idx, "--status-dot": statusTone.fg }}
              >
                <Box className={classes.mobileCardHeader}>
                  <Box className={classes.mobileCardHeaderText}>
                    <span className={classes.mobileCardMeta}>
                      {status === "scheduled" && scheduledLabel
                        ? `${i18n.t("admin.broadcast.scheduledFor", {
                            defaultValue: "SCHEDULED",
                          })} · ${scheduledLabel}`
                        : `${i18n.t("admin.broadcast.sentOn", {
                            defaultValue: "SENT",
                          })} · ${sentDateLabel}`}
                    </span>
                    <Typography
                      className={classes.mobileCardTitle}
                      component="div"
                    >
                      {row.title}
                    </Typography>
                  </Box>
                  <span
                    className={classes.mobileStatusPill}
                    style={{
                      background: statusTone.bg,
                      color: statusTone.fg,
                    }}
                  >
                    {statusTone.label}
                  </span>
                </Box>

                {preview && (
                  <Typography
                    className={classes.mobileCardBody}
                    component="div"
                  >
                    {preview}
                  </Typography>
                )}

                <Box className={classes.mobileCardAudience}>
                  <span className={classes.mobileCardAudienceSub}>
                    {i18n.t("admin.broadcast.recipientsLabel", {
                      defaultValue: "Audience",
                    })}
                  </span>
                  <span className={classes.mobileCardAudienceLabel}>
                    {recipientName}
                  </span>
                </Box>

                <Box className={classes.mobileCardFooter}>
                  <Box className={classes.mobileKpi}>
                    <span className={classes.mobileKpiLabel}>
                      {i18n.t("admin.broadcast.kpi.recipients", {
                        defaultValue: "Recipients",
                      })}
                    </span>
                    <span className={classes.mobileKpiValue}>
                      {row.audience === "all"
                        ? i18n.t("admin.broadcast.kpi.allTenants", {
                            defaultValue: "All",
                          })
                        : "1"}
                    </span>
                  </Box>
                  <Box className={classes.mobileKpi}>
                    <span className={classes.mobileKpiLabel}>
                      {i18n.t("admin.broadcast.kpi.severity", {
                        defaultValue: "Severity",
                      })}
                    </span>
                    <span className={classes.mobileKpiValue}>
                      {row.severity}
                    </span>
                  </Box>
                  <Box className={classes.mobileCardActions}>
                    <Tooltip
                      title={
                        row.active
                          ? i18n.t("admin.broadcast.tooltip.active", {
                              defaultValue: "Active (tap to stop)",
                            })
                          : i18n.t("admin.broadcast.tooltip.stopped", {
                              defaultValue: "Stopped (tap to activate)",
                            })
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(row)}
                        role="switch"
                        aria-checked={!!row.active}
                        aria-label={
                          row.active
                            ? i18n.t("admin.broadcast.aria.active", {
                                defaultValue: "Broadcast is active",
                              })
                            : i18n.t("admin.broadcast.aria.stopped", {
                                defaultValue: "Broadcast is stopped",
                              })
                        }
                        style={{ color: row.active ? "#25d366" : "#94a3b8" }}
                      >
                        <PowerSettingsNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(row)}
                      data-variant="danger"
                      aria-label={i18n.t("admin.broadcast.deleteAria", {
                        defaultValue: "Delete broadcast",
                      })}
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Mobile floating compose CTA */}
      <Button
        className={classes.composeFab}
        onClick={() => setComposeOpen(true)}
        aria-label={i18n.t("admin.broadcast.compose", {
          defaultValue: "New broadcast",
        })}
      >
        <AddRoundedIcon />
      </Button>

      {/* Compose dialog */}
      <Dialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {i18n.t("admin.broadcast.compose", { defaultValue: "New broadcast" })}
        </DialogTitle>
        <DialogContent dividers>
          <Box className={classes.composeBox}>
            <TextField
              autoFocus
              label={i18n.t("admin.broadcast.form.title", {
                defaultValue: "Title",
              })}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ maxLength: 200, ...MOBILE_INPUT_PROPS }}
            />
            <TextField
              label={i18n.t("admin.broadcast.form.body", {
                defaultValue: "Body",
              })}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              fullWidth
              variant="outlined"
              size="small"
              multiline
              minRows={3}
              maxRows={6}
              inputProps={{ maxLength: 5000, ...MOBILE_INPUT_PROPS }}
            />
            <Box className={classes.rowControls}>
              <FormControl variant="outlined" size="small">
                <InputLabel>
                  {i18n.t("admin.broadcast.form.severity", {
                    defaultValue: "Severity",
                  })}
                </InputLabel>
                <Select
                  label={i18n.t("admin.broadcast.form.severity", {
                    defaultValue: "Severity",
                  })}
                  value={form.severity}
                  onChange={(e) => setForm({ ...form, severity: e.target.value })}
                >
                  {SEVERITIES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small">
                <InputLabel>
                  {i18n.t("admin.broadcast.form.audience", {
                    defaultValue: "Audience",
                  })}
                </InputLabel>
                <Select
                  label={i18n.t("admin.broadcast.form.audience", {
                    defaultValue: "Audience",
                  })}
                  value={form.audience}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      audience: e.target.value,
                      targetCompanyId:
                        e.target.value === "all" ? "" : form.targetCompanyId,
                    })
                  }
                >
                  <MenuItem value="all">
                    {i18n.t("admin.broadcast.audience.all", {
                      defaultValue: "All tenants",
                    })}
                  </MenuItem>
                  <MenuItem value="tenant">
                    {i18n.t("admin.broadcast.audience.tenant", {
                      defaultValue: "Specific tenant",
                    })}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            {form.audience === "tenant" && (
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>
                  {i18n.t("admin.broadcast.form.target", {
                    defaultValue: "Target company",
                  })}
                </InputLabel>
                <Select
                  label={i18n.t("admin.broadcast.form.target", {
                    defaultValue: "Target company",
                  })}
                  value={form.targetCompanyId}
                  onChange={(e) =>
                    setForm({ ...form, targetCompanyId: e.target.value })
                  }
                >
                  {allCompanies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label={i18n.t("admin.broadcast.form.endsAt", {
                defaultValue: "Expires on (optional)",
              })}
              type="date"
              value={form.endsAt}
              onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{ min: new Date().toISOString().slice(0, 10), ...MOBILE_INPUT_PROPS }}
            />
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActionsMobile}>
          <Button onClick={() => setComposeOpen(false)}>
            {i18n.t("admin.broadcast.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            className={classes.primary}
            onClick={handleCompose}
            disabled={submitting}
          >
            {submitting
              ? i18n.t("admin.broadcast.publishing", {
                  defaultValue: "Publishing…",
                })
              : i18n.t("admin.broadcast.publish", {
                  defaultValue: "Publish",
                })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BroadcastManager;
